import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { formatBathrooms, formatBedroomsLabel } from '../../utils/formatters';

const UnitManagement = ({ units = [], onUnitsChange }) => {
  const [openUnitDialog, setOpenUnitDialog] = useState(false);
  const [editIndex, setEditIndex] = useState(null); // 新增：编辑索引
  const [newUnit, setNewUnit] = useState({
    unitNumber: '',
    squareFeet: '',
    price: '',
    bedrooms: '',
    bathrooms: '',
    availableDate: '',
  });
  const [errors, setErrors] = useState({}); // 新增：错误提示

  // 确保units是数组
  const safeUnits = Array.isArray(units) ? units : [];

  const handleDeleteUnit = (unitIndex) => {
    const updatedUnits = safeUnits.filter((_, i) => i !== unitIndex);
    onUnitsChange(updatedUnits);
  };

  const validate = () => {
    const err = {};
    if (!newUnit.unitNumber) err.unitNumber = 'Required';
    if (!newUnit.squareFeet) err.squareFeet = 'Required';
    if (!newUnit.price) err.price = 'Required';
    if (newUnit.bedrooms === '' || newUnit.bedrooms === null || newUnit.bedrooms === undefined) {
      err.bedrooms = 'Required';
    }
    if (newUnit.bathrooms === '' || newUnit.bathrooms === null || newUnit.bathrooms === undefined) {
      err.bathrooms = 'Required';
    }
    if (!newUnit.availableDate) err.availableDate = 'Required';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleOpenAdd = () => {
    setEditIndex(null);
    setNewUnit({
      unitNumber: '',
      squareFeet: '',
      price: '',
      bedrooms: '',
      bathrooms: '',
      availableDate: '',
    });
    setErrors({});
    setOpenUnitDialog(true);
  };

  const handleOpenEdit = (unit, idx) => {
    setEditIndex(idx);
    setNewUnit({ ...unit });
    setErrors({});
    setOpenUnitDialog(true);
  };

  const handleSave = () => {
    if (!validate()) return;
    if (editIndex !== null) {
      // 编辑
      const updated = [...safeUnits];
      updated[editIndex] = { ...newUnit };
      onUnitsChange(updated);
    } else {
      // 新增
      onUnitsChange([...safeUnits, { ...newUnit }]);
    }
    setOpenUnitDialog(false);
  };

  return (
    <>
      <Typography variant="h6" gutterBottom sx={{ mt: 4, mb: 3 }}>
        Unit Management
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
        >
          Add New Unit
        </Button>
      </Box>

      {/* Units List */}
      {safeUnits.map((unit, index) => (
        <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Unit #{unit.unitNumber}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Square Feet: {unit.squareFeet}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Price: ${unit.price}/month
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Bedrooms: {formatBedroomsLabel(unit.bedrooms)}
              </Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography variant="body2" color="text.secondary">
                Bathrooms: {formatBathrooms(unit.bathrooms)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Available From: {unit.availableDate}
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                color="primary"
                onClick={() => handleOpenEdit(unit, index)}
              >
                Edit
              </Button>
              <Button
                color="error"
                onClick={() => handleDeleteUnit(index)}
              >
                Delete Unit
              </Button>
            </Grid>
          </Grid>
        </Paper>
      ))}

      {/* Add/Edit Unit Dialog */}
      <Dialog open={openUnitDialog} onClose={() => setOpenUnitDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editIndex !== null ? 'Edit Unit' : 'Add New Unit'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Unit Number"
                value={newUnit.unitNumber}
                onChange={(e) => setNewUnit(prev => ({ ...prev, unitNumber: e.target.value }))}
                required
                error={!!errors.unitNumber}
                helperText={errors.unitNumber && <span style={{ color: 'red' }}>{errors.unitNumber}</span>}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Square Feet"
                type="number"
                value={newUnit.squareFeet}
                onChange={(e) => setNewUnit(prev => ({ ...prev, squareFeet: e.target.value }))}
                required
                error={!!errors.squareFeet}
                helperText={errors.squareFeet && <span style={{ color: 'red' }}>{errors.squareFeet}</span>}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Monthly Rent"
                type="number"
                value={newUnit.price}
                onChange={(e) => setNewUnit(prev => ({ ...prev, price: e.target.value }))}
                required
                error={!!errors.price}
                helperText={errors.price && <span style={{ color: 'red' }}>{errors.price}</span>}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Bedrooms"
                type="number"
                value={newUnit.bedrooms}
                onChange={(e) => setNewUnit(prev => ({ ...prev, bedrooms: e.target.value }))}
                required
                error={!!errors.bedrooms}
                helperText={
                  <span>
                    {errors.bedrooms ? (
                      <span style={{ color: 'red', display: 'block' }}>{errors.bedrooms}</span>
                    ) : null}
                    <span style={{ opacity: 0.7, fontSize: '0.75rem' }}>If it's a studio, please enter 0 bedrooms 1 bathroom.</span>
                  </span>
                }
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Bathrooms"
                type="number"
                value={newUnit.bathrooms}
                onChange={(e) => setNewUnit(prev => ({ ...prev, bathrooms: e.target.value }))}
                required
                error={!!errors.bathrooms}
                helperText={errors.bathrooms && <span style={{ color: 'red' }}>{errors.bathrooms}</span>}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Available Date"
                type="date"
                value={newUnit.availableDate}
                onChange={(e) => setNewUnit(prev => ({ ...prev, availableDate: e.target.value }))}
                required
                InputLabelProps={{ shrink: true }}
                error={!!errors.availableDate}
                helperText={errors.availableDate && <span style={{ color: 'red' }}>{errors.availableDate}</span>}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUnitDialog(false)}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
          >
            {editIndex !== null ? 'Save' : 'Add Unit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UnitManagement; 